import React, { useMemo } from 'react';
import { usePlatform } from '../context/PlatformContext';
import { motion } from 'framer-motion';
import {
  FiBarChart2, FiTrendingUp, FiUsers, FiAward, FiMail,
  FiShield, FiLayers, FiCalendar, FiCheckCircle, FiClock,
  FiActivity, FiZap
} from 'react-icons/fi';

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.25, 0.1, 0.25, 1] } }
};
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.07 } } };

export default function GlobalAnalytics() {
  const { programs, categories, batches, candidates, emailHistory } = usePlatform();

  const metrics = useMemo(() => {
    const totalPrograms = programs.length;
    const totalBatches = batches.length;
    const totalCandidates = candidates.length;
    const activeInterns = candidates.filter(c => c.candidateStatus === 'Active' || c.candidateStatus === 'Selected').length;
    const completedInterns = candidates.filter(c => c.candidateStatus === 'Completed').length;
    const generatedDocs = candidates.filter(c => c.docStatus === 'generated').length;
    const emailsSent = emailHistory.filter(e => e.status === 'sent').length;
    const emailsFailed = emailHistory.filter(e => e.status === 'failed').length;
    return {
      totalPrograms, totalBatches, totalCandidates, activeInterns, completedInterns,
      generatedDocs, emailsSent, emailsFailed,
      emailSuccessRate: emailsSent + emailsFailed > 0 ? Math.round((emailsSent / (emailsSent + emailsFailed)) * 100) : 100,
      docRate: totalCandidates > 0 ? Math.round((generatedDocs / totalCandidates) * 100) : 100,
    };
  }, [programs, batches, candidates, emailHistory]);

  const categoryStats = useMemo(() => {
    return categories.map(cat => {
      const progs = programs.filter(p => p.categoryId === cat.id);
      const progIds = new Set(progs.map(p => p.id));
      const cands = candidates.filter(c => progIds.has(c.programId));
      const completed = cands.filter(c => c.candidateStatus === 'Completed').length;
      return { id: cat.id, name: cat.name, color: cat.color || '#0ea5e9', programsCount: progs.length, candidatesCount: cands.length, completedCount: completed };
    });
  }, [categories, programs, candidates]);

  return (
    <motion.div className="space-y-8 pb-16" initial="hidden" animate="visible" variants={stagger}>

      {/* ── Hero Header ─── */}
      <motion.div variants={fadeUp}
        className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 border border-slate-800 shadow-2xl p-10">
        <div className="absolute -top-16 left-1/4 w-72 h-72 bg-violet-600/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-10 right-1/3 w-56 h-56 bg-blue-600/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-6 right-8 opacity-[0.05] pointer-events-none">
          <FiBarChart2 style={{ width: 160, height: 160, color: 'white' }} />
        </div>
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-11 h-11 rounded-2xl bg-violet-600/20 border border-violet-500/30 flex items-center justify-center">
              <FiBarChart2 className="w-5 h-5 text-violet-400" />
            </div>
            <div>
              <div className="text-violet-400 font-black text-[10px] uppercase tracking-[0.25em]">Innovation Hacks</div>
              <div className="text-slate-500 text-[10px] font-bold uppercase tracking-widest">Platform Intelligence Dashboard</div>
            </div>
          </div>
          <h1 className="text-4xl font-black text-white mb-2 leading-tight">
            Global <span className="text-violet-400">Analytics</span>
          </h1>
          <p className="text-slate-400 text-sm font-semibold max-w-xl leading-relaxed">
            Cross-program enrollment data, document lifecycle telemetry, email delivery rates, and category-level distributions — all in one view.
          </p>
        </div>
      </motion.div>

      {/* ── Primary Metric Cards ─── */}
      <motion.div variants={fadeUp} className="grid grid-cols-2 md:grid-cols-4 gap-5">
        {[
          { label: 'Total Programs', val: metrics.totalPrograms, icon: FiLayers, color: 'text-blue-600', iconBg: 'bg-blue-50', iconBorder: 'border-blue-200', valColor: 'text-blue-700' },
          { label: 'Total Candidates', val: metrics.totalCandidates, icon: FiUsers, color: 'text-violet-600', iconBg: 'bg-violet-50', iconBorder: 'border-violet-200', valColor: 'text-violet-700' },
          { label: 'Documents Issued', val: metrics.generatedDocs, icon: FiAward, color: 'text-emerald-600', iconBg: 'bg-emerald-50', iconBorder: 'border-emerald-200', valColor: 'text-emerald-700' },
          { label: 'Emails Dispatched', val: metrics.emailsSent, icon: FiMail, color: 'text-indigo-600', iconBg: 'bg-indigo-50', iconBorder: 'border-indigo-200', valColor: 'text-indigo-700' },
        ].map((s, i) => (
          <motion.div key={s.label}
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07, duration: 0.4 }}
            className="bg-white border-2 border-slate-200 rounded-3xl p-7 hover:border-slate-300 hover:shadow-lg transition-all group">
            <div className={`w-12 h-12 rounded-2xl ${s.iconBg} border-2 ${s.iconBorder} ${s.color} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>
              <s.icon className="w-6 h-6" />
            </div>
            <div className={`text-4xl font-black ${s.valColor} mb-1`}>{s.val}</div>
            <div className="text-slate-600 text-xs font-black uppercase tracking-widest">{s.label}</div>
          </motion.div>
        ))}
      </motion.div>

      {/* ── Secondary Stats Row ─── */}
      <motion.div variants={fadeUp} className="grid grid-cols-2 md:grid-cols-4 gap-5">
        {[
          { label: 'Total Batches', val: metrics.totalBatches, icon: FiCalendar, accent: 'bg-orange-500' },
          { label: 'Active Interns', val: metrics.activeInterns, icon: FiActivity, accent: 'bg-blue-500' },
          { label: 'Completed', val: metrics.completedInterns, icon: FiCheckCircle, accent: 'bg-emerald-500' },
          { label: 'Email Failures', val: metrics.emailsFailed, icon: FiZap, accent: 'bg-red-500' },
        ].map(s => (
          <div key={s.label} className="bg-white border-2 border-slate-200 rounded-2xl p-5 flex items-center gap-4 hover:border-slate-300 transition-all">
            <div className={`w-10 h-10 rounded-xl ${s.accent} flex items-center justify-center flex-shrink-0`}>
              <s.icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-black text-slate-950 text-2xl leading-none">{s.val}</div>
              <div className="text-slate-500 text-[10px] font-black uppercase tracking-widest mt-0.5">{s.label}</div>
            </div>
          </div>
        ))}
      </motion.div>

      {/* ── Rates + Category side by side ─── */}
      <motion.div variants={fadeUp} className="grid grid-cols-1 md:grid-cols-3 gap-6">

        {/* Rate Cards */}
        {[
          {
            title: 'Document Generation Rate',
            value: metrics.docRate + '%',
            desc: 'Proportion of enrolled candidates with generated offer letters or certificates.',
            accent: 'bg-blue-500',
            track: 'bg-blue-100',
            bar: metrics.docRate,
            valColor: 'text-blue-700',
          },
          {
            title: 'Email Delivery Rate',
            value: metrics.emailSuccessRate + '%',
            desc: 'Transactional delivery success via Brevo REST API v3 across all batches.',
            accent: 'bg-emerald-500',
            track: 'bg-emerald-100',
            bar: metrics.emailSuccessRate,
            valColor: 'text-emerald-700',
          },
          {
            title: 'Verification Integrity',
            value: '100%',
            desc: 'QR-code authenticity guarantee on every issued document — zero tampering.',
            accent: 'bg-violet-500',
            track: 'bg-violet-100',
            bar: 100,
            valColor: 'text-violet-700',
          },
        ].map(card => (
          <div key={card.title} className="bg-white border-2 border-slate-200 rounded-3xl p-7 space-y-5 hover:border-slate-300 hover:shadow-md transition-all">
            <div className="flex items-center gap-3">
              <div className={`w-1.5 h-6 ${card.accent} rounded-full`} />
              <h3 className="font-black text-slate-950 text-sm">{card.title}</h3>
            </div>
            <div className={`text-4xl font-black ${card.valColor}`}>{card.value}</div>
            {/* Progress Bar */}
            <div className={`h-3 ${card.track} rounded-full overflow-hidden`}>
              <motion.div
                className={`h-full ${card.accent} rounded-full`}
                initial={{ width: 0 }}
                animate={{ width: card.bar + '%' }}
                transition={{ duration: 1, delay: 0.3, ease: 'easeOut' }}
              />
            </div>
            <p className="text-slate-500 text-xs font-semibold leading-relaxed">{card.desc}</p>
          </div>
        ))}
      </motion.div>

      {/* ── Category Distribution ─── */}
      <motion.div variants={fadeUp} className="bg-white border-2 border-slate-200 rounded-3xl overflow-hidden shadow-sm">
        <div className="px-8 py-5 border-b-2 border-slate-200 flex items-center gap-4">
          <div className="w-11 h-11 rounded-2xl bg-slate-950 flex items-center justify-center flex-shrink-0">
            <FiTrendingUp className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="font-black text-slate-950 text-base">Category-Wise Distribution</h2>
            <p className="text-slate-500 text-xs font-semibold">Programs, candidates, and completion rates by domain category</p>
          </div>
          <div className="ml-auto bg-slate-100 border-2 border-slate-200 text-slate-700 font-black text-xs px-4 py-2 rounded-xl">
            {categories.length} categories
          </div>
        </div>

        {categoryStats.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-slate-500 font-semibold text-sm">
            No categories found. Create a program category to see distribution data.
          </div>
        ) : (
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            {categoryStats.map((cat, i) => (
              <motion.div key={cat.id}
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                className="p-6 bg-slate-50 border-2 border-slate-200 rounded-2xl space-y-4 hover:border-slate-300 hover:shadow-sm transition-all">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="w-4 h-4 rounded-full flex-shrink-0 shadow-sm" style={{ backgroundColor: cat.color }} />
                    <span className="font-black text-slate-950 text-sm">{cat.name}</span>
                  </div>
                  <span className="bg-white border-2 border-slate-200 text-slate-700 font-black text-[10px] uppercase tracking-wider px-3 py-1 rounded-lg">
                    {cat.programsCount} {cat.programsCount === 1 ? 'Program' : 'Programs'}
                  </span>
                </div>
                {/* Stats row */}
                <div className="flex items-center gap-6">
                  <div>
                    <div className="font-black text-slate-950 text-2xl">{cat.candidatesCount}</div>
                    <div className="text-slate-500 text-[10px] font-black uppercase tracking-widest">Enrolled</div>
                  </div>
                  <div className="w-px h-8 bg-slate-200" />
                  <div>
                    <div className="font-black text-emerald-700 text-2xl">{cat.completedCount}</div>
                    <div className="text-slate-500 text-[10px] font-black uppercase tracking-widest">Completed</div>
                  </div>
                  <div className="flex-1 text-right">
                    <div className="font-black text-slate-700 text-lg">
                      {cat.candidatesCount > 0 ? Math.round((cat.completedCount / cat.candidatesCount) * 100) : 0}%
                    </div>
                    <div className="text-slate-500 text-[10px] font-black uppercase tracking-widest">Rate</div>
                  </div>
                </div>
                {/* Progress bar */}
                {cat.candidatesCount > 0 && (
                  <div className="h-2.5 bg-slate-200 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full rounded-full"
                      style={{ backgroundColor: cat.color }}
                      initial={{ width: 0 }}
                      animate={{ width: (cat.completedCount / cat.candidatesCount) * 100 + '%' }}
                      transition={{ duration: 0.9, delay: 0.2 + i * 0.05, ease: 'easeOut' }}
                    />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
